import React, { createContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { apiInstance, routes } from "../../helpers/API";
import { generalActions } from "../../store/actions";
import keyEventsService from "../../store/services/keyEvents.service";
import moment from "moment";
import { getCookie, setCookie } from "../../helpers/cookieUtils";

const SharedTimelineContext = createContext();

const SharedTimelineContextProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [displayYearRange, setdisplayYearRange] = useState({start: moment().year(), end: moment().year()});
  const [tabView, setTabView] = useState("month");
  const [todayTrigger, setTodayTrigger] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [timelineData, setTimelineData] = useState([]);
  const [timelineKeyEvents, setTimelineKeyEvents] = useState([]);
  const [timelineMilestones, setTimelineMilestones] = useState([]);
  // Portfolio only: Projects hanging directly off the Portfolio with no Program parent - not
  // rendered as Gantt rows, only offered as Key Event "attach to" picker targets.
  const [timelineDirectProjects, setTimelineDirectProjects] = useState([]);
  const [entityID, setEntityID] = useState(null);
  const [entityType, setEntityType] = useState("program");
  const [keyEventsLoading, setKeyEventsLoading] = useState(false);
  // Project timeline only: when on, critical tasks are highlighted red (see CriticalPathButton).
  const [criticalPathHighlight, setCriticalPathHighlight] = useState(false);
  const settings = JSON.parse(localStorage.getItem("settings"));

  // "program" and "projectTimeline" have an extra Scope/TaskGroup depth beyond the Portfolio/Project
  // root -> nested-item shape that "portfolio" and "project" stop at (see GanttContainer.jsx).
  const hasTaskLevel = entityType === "program" || entityType === "projectTimeline";

  const [expandedProjects, setExpandedProjects] = useState({});
  const [expandedScopes, setExpandedScopes] = useState({});
  const [expandedTaskGroups, setExpandedTaskGroups] = useState({});
  // Tracks which Excel-style outline level (see setOutlineLevel) the tree currently matches, so the
  // corresponding level button can be highlighted. Individual row chevrons don't update this - it
  // only reflects the last bulk level applied.
  const [outlineLevel, setOutlineLevelState] = useState(null);

  const fetchAndSetTimelineData = async () => {
    try {
      setIsLoading(true);
      if (!entityID) return;

      let endpoint;
      if (entityType === "program") {
        endpoint = routes.getProgramTimeline.replace(":id", entityID);
      } else if (entityType === "project") {
        endpoint = routes.getProjectTimeline.replace(":id", entityID);
      } else if (entityType === "projectTimeline") {
        // Project's own Timeline tab - distinct from "project" above (SummaryTimeLine, used by
        // ProjectManagerDashboard's mini-Gantt) so that endpoint/DTO stays untouched.
        endpoint = routes.getProjectOwnTimeline.replace(":id", entityID);
      } else if (entityType === "portfolio") {
        endpoint = routes.getPortfolioTimeline.replace(":id", entityID);
      }

      const res = await apiInstance.get(
        `${endpoint}?view=${tabView}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage
              ?.getItem("accessToken")
              ?.replace(/"/g, "")}`,
          },
        }
      );

      setdisplayYearRange({start: res?.data?.yearRange?.start, end: res?.data?.yearRange?.end});
      
      setTimelineMilestones(res?.data?.milestones ?? []);
      if (entityType === "program") {
        // Map projects with their scopeItems
        const projects = res?.data?.projects ?? [];
        const mappedProjects = projects.map(project => ({
          ...project,
          scopeItems: project.scopeItems || project.scopes || []
        }));
        setTimelineData(mappedProjects);
        setTimelineKeyEvents(res?.data?.keyEvents ?? []);
      } else if (entityType === "portfolio") {
        setTimelineData(res?.data?.programs ?? []);
        setTimelineDirectProjects(res?.data?.directProjects ?? []);
        setTimelineKeyEvents(res?.data?.keyEvents ?? []);
      } else if (entityType === "projectTimeline") {
        // ProjectTimeLineDto has no top-level "project" row of its own (it's already scoped by
        // url id) - wrap its Scopes under a single always-expanded synthetic row so this reuses
        // the exact same Project->Scope->Task rendering path "program" already has.
        setTimelineData([
          {
            id: entityID,
            name: "Scopes",
            scopeItems: res?.data?.scopes ?? [],
          },
        ]);
        setTimelineKeyEvents(res?.data?.keyEvents ?? []);
      } else {
        setTimelineData(
          res?.data
            ? Array.isArray(res.data)
              ? res.data
              : [res.data]
            : []
        );
      }
    } catch (error) {
      return dispatch(
        generalActions.showToast({
          title: error.message,
          type: "error",
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
      fetchAndSetTimelineData();
  }, [tabView, entityID, entityType]);

  // Outline-level preference (see setOutlineLevel) is remembered per entity type, so reopening a
  // Portfolio Roadmap or a Program/Project Timeline restores whatever collapse depth was last used
  // there. Stored as a cookie, same as this Gantt's sidebar/column widths (GanttContainer.jsx), since
  // it's a pure per-browser UI preference, not server state.
  const OUTLINE_LEVEL_COOKIE_PREFIX = "timelineOutlineLevel_";

  const getSavedOutlineLevel = (type) => {
    const parsed = parseInt(getCookie(`${OUTLINE_LEVEL_COOKIE_PREFIX}${type}`), 10);
    return Number.isFinite(parsed) ? parsed : null;
  };

  // Excel-style outline levels: level N shows rows down to depth N and collapses everything deeper
  // than that, for every row at once. Portfolio/"project" only have 2 depths (root, nested item);
  // Program/ProjectTimeline have 4 (root, nested item, task group, task).
  const applyOutlineLevel = (level, data, taskLevelAllowed) => {
    const nextExpandedProjects = {};
    data.forEach((item) => {
      nextExpandedProjects[item.id] = level >= 2;
    });
    setExpandedProjects(nextExpandedProjects);

    if (taskLevelAllowed) {
      const nextExpandedScopes = {};
      const nextExpandedTaskGroups = {};
      data.forEach((item) => {
        const scopes = item.scopeItems || item.scopes || [];
        scopes.forEach((scope) => {
          nextExpandedScopes[scope.id] = level >= 3;
          (scope.taskGroups || []).forEach((group) => {
            nextExpandedTaskGroups[group.id] = level >= 4;
          });
        });
      });
      setExpandedScopes(nextExpandedScopes);
      setExpandedTaskGroups(nextExpandedTaskGroups);
    }

    setOutlineLevelState(level);
  };

  // Apply the saved outline level (falling back to fully expanded) whenever fresh timeline data
  // loads.
  useEffect(() => {
    if (!timelineData || timelineData.length === 0) {
      setExpandedProjects({});
      setExpandedScopes({});
      setExpandedTaskGroups({});
      setOutlineLevelState(null);
      return;
    }

    const maxLevel = hasTaskLevel ? 4 : 2;
    const saved = getSavedOutlineLevel(entityType);
    const level = saved && saved >= 1 && saved <= maxLevel ? saved : maxLevel;
    applyOutlineLevel(level, timelineData, hasTaskLevel);
  }, [timelineData, entityType]);

  const toggleProject = (projectId) => {
    setExpandedProjects((prev) => ({ ...prev, [projectId]: !prev[projectId] }));
  };

  const toggleScope = (scopeId) => {
    setExpandedScopes((prev) => ({ ...prev, [scopeId]: !prev[scopeId] }));
  };

  const toggleTaskGroup = (taskGroupId) => {
    setExpandedTaskGroups((prev) => ({ ...prev, [taskGroupId]: !prev[taskGroupId] }));
  };

  const setOutlineLevel = (level) => {
    applyOutlineLevel(level, timelineData, hasTaskLevel);
    setCookie(`${OUTLINE_LEVEL_COOKIE_PREFIX}${entityType}`, level);
  };

  // Key Event CRUD lives here (not in timeline.jsx) since Timeline/SharedTimelineContextProvider
  // are mounted in more places than just the Portfolio/Program/Project overview tabs.
  const createKeyEvent = async (payload) => {
    try {
      setKeyEventsLoading(true);
      await keyEventsService.createKeyEvent(entityType, entityID, payload);
      dispatch(generalActions.showToast({ title: "Key event created successfully", type: "success" }));
      await fetchAndSetTimelineData();
    } catch (error) {
      dispatch(generalActions.showToast({ title: error?.response?.data?.message || "Failed to create key event", type: "error" }));
    } finally {
      setKeyEventsLoading(false);
    }
  };

  const updateKeyEvent = async (eventId, payload) => {
    try {
      setKeyEventsLoading(true);
      await keyEventsService.updateKeyEvent(entityType, entityID, eventId, payload);
      dispatch(generalActions.showToast({ title: "Key event updated successfully", type: "success" }));
      await fetchAndSetTimelineData();
    } catch (error) {
      dispatch(generalActions.showToast({ title: error?.response?.data?.message || "Failed to update key event", type: "error" }));
    } finally {
      setKeyEventsLoading(false);
    }
  };

  const deleteKeyEvent = async (eventId) => {
    try {
      setKeyEventsLoading(true);
      await keyEventsService.deleteKeyEvent(entityType, entityID, eventId);
      dispatch(generalActions.showToast({ title: "Key event deleted successfully", type: "success" }));
      await fetchAndSetTimelineData();
    } catch (error) {
      dispatch(generalActions.showToast({ title: error?.response?.data?.message || "Failed to delete key event", type: "error" }));
    } finally {
      setKeyEventsLoading(false);
    }
  };

  return (
    <SharedTimelineContext.Provider
      value={{
        entityID,
        setEntityID,
        entityType,
        setEntityType,
        displayYearRange,
        tabView,
        todayTrigger,
        isLoading,
        setTabView,
        setTodayTrigger,
        fetchAndSetTimelineData,
        timelineData,
        timelineKeyEvents,
        timelineDirectProjects,
        setTimelineData,
        timelineMilestones,
        settings,
        createKeyEvent,
        updateKeyEvent,
        deleteKeyEvent,
        keyEventsLoading,
        expandedProjects,
        expandedScopes,
        expandedTaskGroups,
        toggleProject,
        toggleScope,
        toggleTaskGroup,
        outlineLevel,
        setOutlineLevel,
        hasTaskLevel,
        criticalPathHighlight,
        setCriticalPathHighlight,
      }}
    >
      {children}
    </SharedTimelineContext.Provider>
  );
};

export { SharedTimelineContext, SharedTimelineContextProvider };